import { useRef, useLayoutEffect } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useNavigate } from 'react-router-dom';
import { Star, MapPin, Briefcase, ChevronRight } from 'lucide-react';

gsap.registerPlugin(ScrollTrigger);

const filterChips = ['CTO', 'CISO', 'Fintech', 'SAP', 'AI', 'Available now'];

export default function CXOProfilesSection() {
  const navigate = useNavigate();
  const sectionRef = useRef<HTMLElement>(null);
  const headlineRef = useRef<HTMLDivElement>(null);
  const profileCardRef = useRef<HTMLDivElement>(null);
  const chipsRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      const scrollTl = gsap.timeline({
        scrollTrigger: {
          trigger: sectionRef.current,
          start: 'top top',
          end: '+=130%',
          pin: true,
          scrub: 0.6,
        },
      });

      // Phase 1 (0-30%): Entrance
      // Headline from left
      scrollTl.fromTo(
        headlineRef.current,
        { x: '-18vw', opacity: 0 },
        { x: 0, opacity: 1, ease: 'none' },
        0
      );

      // Profile card from right
      scrollTl.fromTo(
        profileCardRef.current,
        { x: '18vw', y: '8vh', opacity: 0, rotateZ: 2 },
        { x: 0, y: 0, opacity: 1, rotateZ: 0, ease: 'none' },
        0.06
      );

      // Filter chips
      scrollTl.fromTo(
        chipsRef.current,
        { y: -12, opacity: 0 },
        { y: 0, opacity: 1, ease: 'none' },
        0.18
      );

      // Phase 2 (30-70%): Settle - no animation

      // Phase 3 (70-100%): Exit
      scrollTl.fromTo(
        headlineRef.current,
        { y: 0, opacity: 1 },
        { y: '-12vh', opacity: 0, ease: 'power2.in' },
        0.7
      );

      scrollTl.fromTo(
        profileCardRef.current,
        { x: 0, y: 0, opacity: 1 },
        { x: '-10vw', y: '10vh', opacity: 0, ease: 'power2.in' },
        0.7
      );

      scrollTl.fromTo(
        chipsRef.current,
        { opacity: 1 },
        { opacity: 0, ease: 'power2.in' },
        0.75
      );
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section
      ref={sectionRef}
      id="cxo-profiles"
      className="section-pinned bg-navy-light flex items-center z-30"
    >
      <div className="w-full px-6 lg:px-12">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Left - Headline */}
          <div ref={headlineRef} className="opacity-0">
            <span className="font-mono text-xs uppercase tracking-[0.2em] text-teal mb-4 block">
              Marketplace
            </span>
            <h2 className="font-heading text-h2 sm:text-h1 font-bold text-white mb-6">
              Browse verified CXO profiles
            </h2>
            <p className="text-body-lg text-slate mb-8 max-w-lg">
              Filter by function, industry, language, and availability. Shortlist
              and compare side-by-side.
            </p>
            <div className="flex items-center gap-4">
              <button 
                onClick={() => navigate('/register')}
                className="btn-primary flex items-center gap-2"
              >
                Explore CXOs
                <ChevronRight className="w-4 h-4" />
              </button>
              <button 
                onClick={() => navigate('/register')}
                className="btn-secondary"
              >
                View comparison tool
              </button>
            </div>
          </div>

          {/* Right - Profile Card + Filters */}
          <div className="relative">
            {/* Filter Chips */}
            <div
              ref={chipsRef}
              className="flex flex-wrap gap-2 mb-4 opacity-0"
            >
              {filterChips.map((chip) => (
                <span
                  key={chip}
                  className="px-3 py-1.5 text-xs font-medium bg-white/5 border border-white/10 rounded-full text-slate hover:border-teal/50 hover:text-teal transition-colors cursor-pointer"
                >
                  {chip}
                </span>
              ))}
            </div>

            {/* Profile Card */}
            <div
              ref={profileCardRef}
              className="surface-panel p-6 lg:p-8 opacity-0"
            >
              {/* Header */}
              <div className="flex items-start gap-4 mb-6">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-teal/20 to-teal/5 flex items-center justify-center flex-shrink-0">
                  <span className="font-heading text-2xl font-bold text-teal">
                    DK
                  </span>
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-heading text-xl font-semibold text-white">
                      David Kim
                    </h3>
                    <span className="px-2 py-0.5 text-xs font-medium bg-teal/20 text-teal rounded-full">
                      Verified
                    </span>
                  </div>
                  <p className="text-slate text-sm mb-2">
                    Former CTO at Grab, 15+ years in fintech
                  </p>
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-1">
                      <Star className="w-4 h-4 text-teal fill-teal" />
                      <span className="text-sm text-white font-medium">4.9</span>
                      <span className="text-sm text-slate">(24 reviews)</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tags */}
              <div className="flex flex-wrap gap-2 mb-6">
                <span className="px-3 py-1 text-xs font-medium bg-white/5 rounded-full text-slate">
                  CTO
                </span>
                <span className="px-3 py-1 text-xs font-medium bg-white/5 rounded-full text-slate">
                  Fintech
                </span>
                <span className="px-3 py-1 text-xs font-medium bg-white/5 rounded-full text-slate">
                  SaaS
                </span>
                <span className="px-3 py-1 text-xs font-medium bg-white/5 rounded-full text-slate">
                  AI/ML
                </span>
              </div>

              {/* Details */}
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-slate" />
                  <span className="text-sm text-slate">Singapore</span>
                </div>
                <div className="flex items-center gap-2">
                  <Briefcase className="w-4 h-4 text-slate" />
                  <span className="text-sm text-slate">15 years exp</span>
                </div>
              </div>

              {/* Rate & CTA */}
              <div className="flex items-center justify-between pt-4 border-t border-white/10">
                <div>
                  <span className="text-sm text-slate">From</span>
                  <p className="text-2xl font-heading font-bold text-teal">
                    $350<span className="text-sm text-slate font-normal">/hr</span>
                  </p>
                </div>
                <button 
                  onClick={() => navigate('/register')}
                  className="btn-primary"
                >
                  View full profile
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
